import React, { useEffect, useState } from "react";
import { getPageBySlug, getCollection } from "../lib/cms";
import { 
  Check, 
  Info, 
  FileText, 
  Calendar, 
  ShieldCheck, 
  Car, 
  Award, 
  Heart, 
  Users, 
  Sparkles, 
  ArrowRight, 
  Clock, 
  MapPin, 
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Star
} from "lucide-react";
import { Link } from "react-router-dom";

export default function Korekort() {
  const [pageData, setPageData] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullContent, setShowFullContent] = useState(false);
  const [activeTab, setActiveTab] = useState("lovpakken"); // lovpakken, andre-ydelser

  useEffect(() => {
    async function loadData() {
      try {
        const [page, studentList] = await Promise.all([
          getPageBySlug("korekort"),
          getCollection("passed_students")
        ]);
        setPageData(page);
        if (studentList && studentList.length > 0) {
          // Take up to 4 students for social proof
          setStudents(studentList.slice(0, 4));
        }
      } catch (err) {
        console.error("Error loading page data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", pageData.meta_description || "");
      }
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-brand-primary"></div>
          <div className="text-brand-primary font-bold text-lg">Henter personligt indhold...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-light min-h-screen">
      {/* 1. Personal Hero Section */}
      <section className="relative overflow-hidden py-16 lg:py-24 bg-gradient-to-b from-stone-100 to-brand-light border-b border-stone-200/60">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute top-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-brand-primary/10 blur-3xl" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[350px] h-[350px] rounded-full bg-brand-secondary/10 blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Text */}
            <div className="lg:col-span-7 space-y-6">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider">
                <Sparkles className="h-3.5 w-3.5" /> Din personlige vej til kørekortet
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-brand-dark tracking-tight leading-none">
                Tag dit kørekort hos <span className="text-brand-primary">Carina</span> i Gug
              </h1>
              <p className="text-stone-600 text-base sm:text-lg lg:text-xl leading-relaxed max-w-2xl">
                At tage kørekort er en stor milepæl. Hos mig er du ikke bare et nummer i rækken. Jeg lægger vægt på en tålmodig, tryg og personlig køreuddannelse med små teorihold, hvor der er plads til alle dine spørgsmål.
              </p>

              {/* Quick Badges */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
                <div className="flex items-center gap-2.5 text-stone-700 text-sm font-medium">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <Check className="h-3 w-3 text-emerald-600" />
                  </div>
                  <span>Små teorihold (max 8 elever)</span>
                </div>
                <div className="flex items-center gap-2.5 text-stone-700 text-sm font-medium">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <Check className="h-3 w-3 text-emerald-600" />
                  </div>
                  <span>Tålmodig & rolig kørelærer</span>
                </div>
                <div className="flex items-center gap-2.5 text-stone-700 text-sm font-medium">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <Check className="h-3 w-3 text-emerald-600" />
                  </div>
                  <span>Kør i Audi Q3 eller VW ID.3 Elbil</span>
                </div>
                <div className="flex items-center gap-2.5 text-stone-700 text-sm font-medium">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <Check className="h-3 w-3 text-emerald-600" />
                  </div>
                  <span>Høj beståelsesprocent & tryghed</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 pt-4">
                <a 
                  href="#priser" 
                  className="px-6 py-3.5 bg-brand-primary text-white font-bold rounded-xl shadow-lg shadow-brand-primary/20 hover:bg-brand-primary-hover transition-all duration-200 flex items-center gap-2"
                >
                  Se priser & lovpakke <ArrowRight className="h-4 w-4" />
                </a>
                <Link 
                  to="/kontakt" 
                  className="px-6 py-3.5 bg-white text-brand-dark border border-stone-200 font-bold rounded-xl hover:bg-stone-50 transition-all duration-200 flex items-center gap-2"
                >
                  Se næste holdstart
                </Link>
              </div>
            </div>

            {/* Right Column: Image of Car */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-[340px] sm:max-w-[380px] lg:max-w-none">
                {/* Decorative background card */}
                <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-brand-primary to-brand-secondary opacity-10 blur-lg" />
                
                {/* Main Image */}
                <div className="relative rounded-2xl overflow-hidden border-4 border-white shadow-2xl bg-stone-100 aspect-[4/5]">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=90,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/9a7044-321b9f7c775a4e86a5b9f48c2a0bac79-mv2.jpg"
                    alt="Audi Q3 Skolevogn - Carinas Køreskole"
                    className="w-full h-full object-cover"
                  />
                  {/* Floating Badge */}
                  <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md p-4 rounded-xl border border-stone-100 shadow-lg flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
                      <Award className="h-5 w-5 text-brand-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-brand-dark text-sm leading-tight">Audi Q3 Skolevogn</h4>
                      <p className="text-stone-500 text-xs">Lær at køre i sikre og moderne rammer</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Friend Offer Banner */}
      <section className="py-6 bg-brand-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-brand-primary-hover via-brand-primary to-brand-primary opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <div className="flex items-center gap-3.5 flex-col md:flex-row">
            <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center shrink-0 animate-bounce">
              <Heart className="h-6 w-6 text-white fill-white" />
            </div>
            <div>
              <h3 className="font-black text-lg sm:text-xl leading-tight">Tag kørekortet med en ven og SPAR!</h3>
              <p className="text-white/90 text-sm">Tilmeld jer sammen, og I får begge en ekstra <strong className="font-extrabold text-amber-200">GRATIS kørelektion</strong> hver (Værdi 695 kr.).</p>
            </div>
          </div>
          <Link 
            to="/kontakt" 
            className="px-5 py-2.5 bg-white text-brand-primary font-black rounded-lg hover:bg-stone-50 transition-all duration-200 text-sm shrink-0 shadow-md"
          >
            Tilmeld jer nu
          </Link>
        </div>
      </section>

      {/* 3. Vores Skolevogne Section */}
      <section className="py-16 lg:py-24 bg-white border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-brand-primary text-xs font-bold uppercase tracking-wider px-3 py-1 bg-brand-primary/10 rounded-full">
              Skolevognene
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-brand-dark mt-3 mb-4">
              Hvilken bil vil du køre i?
            </h2>
            <p className="text-stone-600 text-base sm:text-lg">
              Hos mig kan du vælge mellem to fantastiske, moderne og sikre skolevogne. Uanset om du vil have manuelt gear eller automatgear, får du en køreoplevelse i absolut topklasse.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* Audi Q3 (Manuel) */}
            <div className="bg-stone-50 rounded-2xl border border-stone-200/60 overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 flex flex-col group">
              <div className="relative h-[220px] sm:h-[260px] overflow-hidden bg-stone-200">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/9a7044-321b9f7c775a4e86a5b9f48c2a0bac79-mv2.jpg" 
                  alt="Audi Q3 Skolevogn" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute top-4 left-4 bg-brand-dark text-white text-xs font-bold px-3 py-1 rounded-full">
                  Manuelt gear
                </span>
              </div>
              <div className="p-6 sm:p-8 flex-grow flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <h3 className="text-xl sm:text-2xl font-black text-brand-dark">Audi Q3 (SUV)</h3>
                  <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                    Vores populære Audi Q3 er perfekt til dig, der ønsker kørekort med manuelt gear. Du sidder højt, har et fantastisk udsyn over vejen og kørekomforten er helt i top. Bilen er utrolig stabil, sikker og nem at manøvrere.
                  </p>
                </div>
                <div className="pt-4 border-t border-stone-200/60 grid grid-cols-2 gap-4 text-xs text-stone-500">
                  <div className="flex items-center gap-1.5">
                    <Car className="h-4 w-4 text-brand-primary" />
                    <span>Høj siddeposition</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="h-4 w-4 text-brand-primary" />
                    <span>Sikkerhed i topklasse</span>
                  </div>
                </div>
              </div>
            </div>

            {/* VW ID.3 (Automatgear) */}
            <div className="bg-stone-50 rounded-2xl border border-stone-200/60 overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 flex flex-col group">
              <div className="relative h-[220px] sm:h-[260px] overflow-hidden bg-stone-200">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/id-3-2.jpeg" 
                  alt="Volkswagen ID.3 Skolevogn" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute top-4 left-4 bg-brand-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                  Automatgear (Elbil)
                </span>
              </div>
              <div className="p-6 sm:p-8 flex-grow flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <h3 className="text-xl sm:text-2xl font-black text-brand-dark">Volkswagen ID.3 Elbil</h3>
                  <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                    Ønsker du kørekort til automatgear? Vores topmoderne, 100% elektriske VW ID.3 er lydløs, miljøvenlig og utrolig sjov og nem at køre. Den er spækket med moderne teknologi, der gør din køreuddannelse til en leg.
                  </p>
                </div>
                <div className="pt-4 border-t border-stone-200/60 grid grid-cols-2 gap-4 text-xs text-stone-500">
                  <div className="flex items-center gap-1.5">
                    <Car className="h-4 w-4 text-brand-primary" />
                    <span>Lydløs & nem kørsel</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="h-4 w-4 text-brand-primary" />
                    <span>100% Grøn elbil</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Lovpakken & Priser Section */}
      <section id="priser" className="py-16 lg:py-24 bg-brand-light border-b border-stone-200/60 scroll-mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-brand-primary text-xs font-bold uppercase tracking-wider px-3 py-1 bg-brand-primary/10 rounded-full">
              Priser & Lovpakke
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-brand-dark mt-3 mb-4">
              Gennemskuelige priser uden skjulte gebyrer
            </h2>
            <p className="text-stone-600 text-base sm:text-lg">
              Her kan du se præcis, hvad der er inkluderet i vores lovpligtige pakke til personbil, samt hvilke yderligere udgifter du skal forvente.
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex p-1 bg-stone-200/80 rounded-xl">
              <button
                onClick={() => setActiveTab("lovpakken")}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${
                  activeTab === "lovpakken"
                    ? "bg-white text-brand-dark shadow-sm"
                    : "text-stone-600 hover:text-brand-dark"
                }`}
              >
                Lovpakken (Personbil)
              </button>
              <button
                onClick={() => setActiveTab("andre-ydelser")}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${
                  activeTab === "andre-ydelser"
                    ? "bg-white text-brand-dark shadow-sm"
                    : "text-stone-600 hover:text-brand-dark"
                }`}
              >
                Andre ydelser (Trailer, Generhvervelse m.m.)
              </button>
            </div>
          </div>

          {activeTab === "lovpakken" ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Lovpakken Card */}
              <div className="lg:col-span-7 bg-white rounded-2xl border border-stone-200 shadow-xl overflow-hidden relative">
                <div className="absolute top-0 left-0 right-0 h-2 bg-brand-primary" />
                <div className="p-6 sm:p-10 space-y-8">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-stone-100">
                    <div>
                      <span className="text-xs font-bold text-brand-primary uppercase tracking-wider">Alt det lovpligtige</span>
                      <h3 className="text-2xl sm:text-3xl font-black text-brand-dark mt-1">Lovpakken</h3>
                      <p className="text-stone-500 text-xs sm:text-sm mt-1">Betales i 2 rater (8.000 kr. ved 1. rate & 6.500 kr. ved 2. rate)</p>
                    </div>
                    <div className="text-left sm:text-right">
                      <span className="text-3xl sm:text-4xl font-black text-brand-dark block">14.500 kr.</span>
                      <span className="text-stone-500 text-xs">Inkl. moms og alle lovpligtige baner</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-bold text-brand-dark text-sm uppercase tracking-wider">Hvad indeholder lovpakken?</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="h-3.5 w-3.5 text-brand-primary" />
                        </div>
                        <div>
                          <strong className="font-bold text-brand-dark text-sm block">30 teorilektioner</strong>
                          <span className="text-stone-500 text-xs">Undervisning á 45 min. i hyggelige hold</span>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="h-3.5 w-3.5 text-brand-primary" />
                        </div>
                        <div>
                          <strong className="font-bold text-brand-dark text-sm block">3 lektioner på manøvrebane</strong>
                          <span className="text-stone-500 text-xs">Kravlegård inkl. leje af bane og bil</span>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="h-3.5 w-3.5 text-brand-primary" />
                        </div>
                        <div>
                          <strong className="font-bold text-brand-dark text-sm block">4 lektioner på glatbane</strong>
                          <span className="text-stone-500 text-xs">Køreteknisk anlæg inkl. leje af bane og bil</span>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="h-3.5 w-3.5 text-brand-primary" />
                        </div>
                        <div>
                          <strong className="font-bold text-brand-dark text-sm block">17 kørelektioner på vej</strong>
                          <span className="text-stone-500 text-xs">Individuel kørsel á 45 min. i rigtig trafik</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-200/60 rounded-xl flex gap-3 text-amber-900">
                    <Info className="h-5 w-5 text-brand-secondary shrink-0 mt-0.5" />
                    <p className="text-xs leading-relaxed">
                      <strong>Automatgear uden merpris:</strong> Du kan vælge at tage dit kørekort i vores VW ID.3 elbil med automatgear (kode 78/148) til nøjagtig samme pris som manuelt gear!
                    </p>
                  </div>
                </div>
              </div>

              {/* Yderligere Udgifter Card */}
              <div className="lg:col-span-5 bg-stone-50 rounded-2xl border border-stone-200 p-6 sm:p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-black text-brand-dark">Yderligere udgifter</h3>
                  <p className="text-stone-500 text-xs mt-1">Udgifter udover lovpakken, som du skal påregne:</p>
                </div>

                <ul className="space-y-3 divide-y divide-stone-200/60">
                  <li className="flex justify-between items-center text-sm pt-3 first:pt-0">
                    <span className="text-stone-600">Leje af skolevogn til køreprøve</span>
                    <strong className="font-bold text-brand-dark">1.000 kr.</strong>
                  </li>
                  <li className="flex justify-between items-center text-sm pt-3">
                    <span className="text-stone-600">Online teori (Drive4you, 3 mdr.)</span>
                    <strong className="font-bold text-brand-dark">300 kr.</strong>
                  </li>
                  <li className="flex justify-between items-center text-sm pt-3">
                    <span className="text-stone-600">Prøvegebyr til Færdselsstyrelsen</span>
                    <strong className="font-bold text-brand-dark">1.600 kr.</strong>
                  </li>
                  <li className="flex justify-between items-center text-sm pt-3">
                    <span className="text-stone-600">Færdselsrelateret førstehjælp</span>
                    <strong className="font-bold text-brand-dark">ca. 700 kr.</strong>
                  </li>
                  <li className="flex justify-between items-center text-sm pt-3">
                    <span className="text-stone-600">Lægeerklæring (hos egen læge)</span>
                    <strong className="font-bold text-brand-dark">ca. 450 kr.</strong>
                  </li>
                  <li className="flex justify-between items-center text-sm pt-3">
                    <span className="text-stone-600">Pasfoto til ansøgning</span>
                    <strong className="font-bold text-brand-dark">ca. 125 kr.</strong>
                  </li>
                  <li className="flex justify-between items-center text-sm pt-3">
                    <span className="text-stone-600">Ekstra kørelektion (hvis behov, á 45 min)</span>
                    <strong className="font-bold text-brand-dark">695 kr.</strong>
                  </li>
                </ul>

                <p className="text-[11px] text-stone-500 leading-relaxed">
                  * Prisen på kørekortet afhænger af den enkelte elevs formåen. Lovpakkens pris er baseret på lovens mindstekrav, og ekstra kørelektioner kan påregnes i samråd med kørelæreren for at sikre, at du er helt klar og tryg.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {/* Generhvervelse */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between space-y-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="text-lg sm:text-xl font-black text-brand-dark">Generhvervelse / Kontrollerende prøve</h3>
                    <span className="text-lg sm:text-xl font-black text-brand-primary shrink-0">4.500 kr.</span>
                  </div>
                  <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                    Har du mistet dit kørekort, eller skal du op til en kontrollerende køreprøve? Vi hjælper dig hurtigt og sikkert igennem forløbet.
                  </p>
                  <ul className="space-y-1.5 text-xs text-stone-500 pt-2">
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> Online adgang til teorimateriale
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> 3 kørelektioner á 45 min.
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> Leje af skolevogn til køreprøven
                    </li>
                  </ul>
                </div>
                <div className="text-[10px] text-stone-400 border-t border-stone-100 pt-3">
                  Ekskl. prøvegebyr til staten (1.210 kr.) og evt. ANT-kursus (3.200 kr. ved spiritus/narko).
                </div>
              </div>

              {/* Særlig Køreundervisning */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between space-y-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="text-lg sm:text-xl font-black text-brand-dark">Særlig køreundervisning</h3>
                    <span className="text-lg sm:text-xl font-black text-brand-primary shrink-0">7.000 kr.</span>
                  </div>
                  <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                    Til dig, der har fået kørselsforbud i de første 3 år med kørekort. Vi sikrer en grundig og pædagogisk genopfriskning.
                  </p>
                  <ul className="space-y-1.5 text-xs text-stone-500 pt-2">
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> 8 teorilektioner
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> 8 kørelektioner på vej
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> Leje af skolevogn til køreprøven
                    </li>
                  </ul>
                </div>
                <div className="text-[10px] text-stone-400 border-t border-stone-100 pt-3">
                  Ekskl. prøvegebyr til staten (1.510 kr.) og pasfoto.
                </div>
              </div>

              {/* Trailerkort (BE) */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between space-y-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="text-lg sm:text-xl font-black text-brand-dark">Trailerkort / BE Kørekort</h3>
                    <span className="text-lg sm:text-xl font-black text-brand-primary shrink-0">5.100 kr.</span>
                  </div>
                  <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                    Skal du køre med stor trailer, campingvogn eller hestetrailer? Tag dit BE-kørekort hurtigt og nemt hos os.
                  </p>
                  <ul className="space-y-1.5 text-xs text-stone-500 pt-2">
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> 4 lektioner teori
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> 6 lektioner på vej
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> Leje af bil og trailer til praktisk prøve
                    </li>
                  </ul>
                </div>
                <div className="text-[10px] text-stone-400 border-t border-stone-100 pt-3">
                  Alle priser er inkl. moms. Undervisningen foregår på dansk.
                </div>
              </div>

              {/* Rutine-lektioner */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between space-y-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="text-lg sm:text-xl font-black text-brand-dark">Rutine-lektioner</h3>
                    <span className="text-lg sm:text-xl font-black text-brand-primary shrink-0">695 kr. <span className="text-xs text-stone-500 font-normal">/ lektion</span></span>
                  </div>
                  <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                    Er det længe siden, du har kørt bil? Føler du dig usikker i rundkørsler eller ved motorvejskørsel? Få fornyet selvtillid bag rattet med en erfaren og rolig kørelærer.
                  </p>
                  <ul className="space-y-1.5 text-xs text-stone-500 pt-2">
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> Individuel lektion á 45 min.
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> Fokus på dine specifikke udfordringer
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand-primary" /> Kørsel i trygge rammer
                    </li>
                  </ul>
                </div>
                <div className="text-[10px] text-stone-400 border-t border-stone-100 pt-3">
                  Perfekt til ældre bilister eller folk med køreangst.
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 5. Sådan foregår det (The Journey) Section */}
      <section className="py-16 lg:py-24 bg-white border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-brand-primary text-xs font-bold uppercase tracking-wider px-3 py-1 bg-brand-primary/10 rounded-full">
              Forløbet
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-brand-dark mt-3 mb-4">
              Din rejse mod kørekortet trin for trin
            </h2>
            <p className="text-stone-600 text-base sm:text-lg">
              Hos mig følger vi en struktureret og pædagogisk plan, så du altid ved, hvad der skal ske, og føler dig tryg hele vejen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-1/4 left-[12%] right-[12%] h-0.5 bg-stone-200 -z-10" />

            {/* Step 1 */}
            <div className="space-y-4 text-center md:text-left">
              <div className="mx-auto md:mx-0 h-12 w-12 rounded-full bg-brand-primary text-white font-black flex items-center justify-center text-lg shadow-lg shadow-brand-primary/20">
                1
              </div>
              <h3 className="text-lg font-black text-brand-dark">Teori i Gug</h3>
              <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                Vi mødes i vores hyggelige teorilokale på Gugvej 186D. Små hold sikrer, at vi kan hygge os med snacks og sodavand, mens vi lærer teorien grundigt sammen.
              </p>
            </div>

            {/* Step 2 */}
            <div className="space-y-4 text-center md:text-left">
              <div className="mx-auto md:mx-0 h-12 w-12 rounded-full bg-brand-primary text-white font-black flex items-center justify-center text-lg shadow-lg shadow-brand-primary/20">
                2
              </div>
              <h3 className="text-lg font-black text-brand-dark">Manøvrebane</h3>
              <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                Inden du skal ud i den rigtige trafik, tager vi på manøvrebane (kravlegård). Her lærer du at styre, bremse og bakke bilen helt uden stress og i sikre rammer.
              </p>
            </div>

            {/* Step 3 */}
            <div className="space-y-4 text-center md:text-left">
              <div className="mx-auto md:mx-0 h-12 w-12 rounded-full bg-brand-primary text-white font-black flex items-center justify-center text-lg shadow-lg shadow-brand-primary/20">
                3
              </div>
              <h3 className="text-lg font-black text-brand-dark">Kørsel på vej</h3>
              <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                Nu skal vi ud på vejene i Gug og Aalborg. Vi starter på rolige villaveje og bygger gradvist på med motorvej, mørkekørsel og svære kryds, i dit helt eget tempo.
              </p>
            </div>

            {/* Step 4 */}
            <div className="space-y-4 text-center md:text-left">
              <div className="mx-auto md:mx-0 h-12 w-12 rounded-full bg-brand-primary text-white font-black flex items-center justify-center text-lg shadow-lg shadow-brand-primary/20">
                4
              </div>
              <h3 className="text-lg font-black text-brand-dark">Glatbane & Prøver</h3>
              <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                Vi tager på køreteknisk anlæg (glatbane) i Viborg eller Støvring, hvorefter du er klar til den digitale teoriprøve og den endelige, spændende køreprøve!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Social Proof Section (Happy Students) */}
      {students.length > 0 && (
        <section className="py-16 lg:py-24 bg-brand-light border-b border-stone-200/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-brand-primary text-xs font-bold uppercase tracking-wider px-3 py-1 bg-brand-primary/10 rounded-full">
                Glade elever
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-brand-dark mt-3 mb-4">
                De har bestået hos Carina
              </h2>
              <p className="text-stone-600 text-base sm:text-lg">
                Se nogle af de mange glade elever, der har taget deres kørekort hos mig og nu kører sikkert rundt på de danske veje.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {students.map((student) => (
                <div 
                  key={student.id} 
                  className="bg-white rounded-2xl border border-stone-200/80 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 flex flex-col"
                >
                  <div className="relative aspect-square bg-stone-100">
                    <img 
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,height=300,fit=cover,quality=85,format=auto/${student.image?.[0]?.split('assets.auxodev.dk/')?.[1] || student.image?.[0]}`} 
                      alt={`Glade elev ${student.name}`} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-2 right-2 bg-brand-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-md">
                      Bestået! 🎉
                    </div>
                  </div>
                  <div className="p-4 flex-grow flex flex-col justify-between space-y-2">
                    <div>
                      <h4 className="font-bold text-brand-dark text-sm">{student.name}</h4>
                      {student.passed_date && (
                        <span className="text-[10px] text-stone-400 block">{student.passed_date}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 7. Hvad du selv skal sørge for Section */}
      <section className="py-16 lg:py-24 bg-white border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-brand-primary text-xs font-bold uppercase tracking-wider px-3 py-1 bg-brand-primary/10 rounded-full">
              Forberedelse
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-brand-dark mt-3 mb-4">
              Hvad skal du selv sørge for?
            </h2>
            <p className="text-stone-600 text-base sm:text-lg">
              Inden vi kan bestille dine prøver og sende din ansøgning afsted, er der et par praktiske ting, du selv skal have styr på.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Lægeerklæring */}
            <div className="p-6 sm:p-8 bg-stone-50 rounded-2xl border border-stone-200/60 space-y-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="h-12 w-12 rounded-xl bg-brand-primary/10 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-brand-primary" />
                </div>
                <h3 className="text-lg font-black text-brand-dark">Lægeerklæring</h3>
                <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                  Bestil tid hos din egen læge til en "kørekort-lægeerklæring" så hurtigt som muligt. Lægen udfylder den og lægger den i en lukket kuvert, som du skal medbringe til teori.
                </p>
              </div>
              <span className="text-xs text-stone-400 block pt-4 border-t border-stone-200/40">Husk at bestille tid i god tid!</span>
            </div>

            {/* Pasfoto */}
            <div className="p-6 sm:p-8 bg-stone-50 rounded-2xl border border-stone-200/60 space-y-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="h-12 w-12 rounded-xl bg-brand-primary/10 flex items-center justify-center">
                  <Award className="h-6 w-6 text-brand-primary" />
                </div>
                <h3 className="text-lg font-black text-brand-dark">Fysisk Pasfoto</h3>
                <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                  Du skal have taget et godkendt pasfoto hos en fotograf eller i en fotoboks. Medbring det fysiske foto til teoritimen, så vi kan vedlægge det din ansøgning.
                </p>
              </div>
              <span className="text-xs text-stone-400 block pt-4 border-t border-stone-200/40">Skal overholde politiets krav til pasfotos.</span>
            </div>

            {/* Førstehjælp */}
            <div className="p-6 sm:p-8 bg-stone-50 rounded-2xl border border-stone-200/60 space-y-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="h-12 w-12 rounded-xl bg-brand-primary/10 flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-brand-primary" />
                </div>
                <h3 className="text-lg font-black text-brand-dark">Førstehjælpskursus</h3>
                <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                  Du skal have et 8-timers færdselsrelateret førstehjælpskursus. Hvis du ikke har et i forvejen (max 1 år gammelt), hjælper jeg dig meget gerne med tilmeldingen.
                </p>
              </div>
              <Link 
                to="/forstehjaelp" 
                className="text-xs text-brand-primary font-bold hover:underline flex items-center gap-1 pt-4 border-t border-stone-200/40"
              >
                Læs mere om førstehjælp <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
